import { useQueryClient } from '@tanstack/react-query'
import type { AxiosRequestConfig } from 'axios'
import Button from 'ext-ui/components/Button/Button'
import LinkText from 'ext-ui/components/LinkText/LinkText'
import Tooltip from 'ext-ui/components/Tooltip/Tooltip'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import AiStarsIcon from 'ext-ui/svg/icon/AiStarsIcon'
import CardsNewIcon from 'ext-ui/svg/icon/CardsNew'
import PolicyInsightThumbsDownIcon from 'ext-ui/svg/icon/PolicyInsightThumbsDownIcon'
import PolicyInsightThumbsUpIcon from 'ext-ui/svg/icon/PolicyInsightThumbsUpIcon'
import ThumbsDownIcon from 'ext-ui/svg/icon/ThumbsDownIcon'
import ThumbsUpIcon from 'ext-ui/svg/icon/ThumbsUpIcon'
import React, { useEffect, useId, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import NormalizedMerchantFeedbackModal from 'src/component-modal/NormalizedMerchantFeedbackModal/NormalizedMerchantFeedbackModal'
import BetaTag from 'src/components/BetaTag/BetaTag'
import {
  useAIPolicyAssistantEnabled,
  useQueryExpensePolicy,
} from 'src/components/ExpensePolicyUploadBox/expensePolicy.queries'
import SvgChevronDown from 'src/components/Svgs/SvgChevronDown'
import SvgChevronUp from 'src/components/Svgs/SvgChevronUp'
import SvgExternalLink from 'src/components/Svgs/SvgExternalLink'
import Tag from 'src/components/Tag/Tag'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { AvatarSizes } from 'src/enums'
import MerchantIcon from 'src/feature/MerchantIcon/MerchantIcon'
import { dateFormatString } from 'src/localization/Dte'
import { getLocaleInfo } from 'src/localization/locales'
import { useTranslate } from 'src/localization/Trns'
import type { TransactionAiInsights } from 'src/models/transaction.models'
import {
  ConnectedPlatformStatus,
  TransactionAiInsightFeedbackType,
  TransactionAiInsightType,
  TransactionDetailModel,
} from 'src/models/transaction.models'
import { UserListModel } from 'src/models/user.models'
import { useRequestConfig } from 'src/queries/queries.utils'
import {
  queryTransactionDetail,
  usePutTransactionAiInsightFeedbackMutation,
} from 'src/queries/transactions.queries'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { convertUTCTimezone } from 'src/utils/date.utils'
import { isExpensePolicyAIEnabled } from 'src/utils/featureFlags'
import { css } from 'styled-components'

import style, { iconStyle } from './TransactionSummarySection.style'

const POLICY_AGENT_DISPLAY_OVERRIDE_INSIGHT_ID = 'override-policy-agent-insight'

const isPolicyAgentInsight = (insight: TransactionAiInsights): boolean =>
  insight.insightType === TransactionAiInsightType.PolicyAgent

const isPersistedPolicyAgentInsight = (
  insight: TransactionAiInsights,
): boolean =>
  isPolicyAgentInsight(insight) &&
  Boolean(insight.id) &&
  insight.id !== POLICY_AGENT_DISPLAY_OVERRIDE_INSIGHT_ID

const findPersistedPolicyAgentInsightForDisplay = (
  policyInsights: TransactionAiInsights[],
  displayInsight: TransactionAiInsights | undefined,
): TransactionAiInsights | undefined => {
  if (!displayInsight) return undefined
  if (isPersistedPolicyAgentInsight(displayInsight)) return displayInsight

  return policyInsights.find(
    (insight) =>
      isPersistedPolicyAgentInsight(insight) &&
      insight.insightText === displayInsight.insightText,
  )
}

const normalizePolicyInsightBulletLine = (line: string): string =>
  /^[•\-–—]\s+/.test(line) ? `• ${line.replace(/^[•\-–—]\s+/, '')}` : line

const isPolicyInsightBulletLine = (line: string): boolean =>
  line.startsWith('• ')

const getPolicyInsightTextLines = (insightText: string): string[] => {
  const explicitLines = insightText
    .split(/\r?\n/)
    .map((part) => part.trim())
    .filter(Boolean)

  if (explicitLines.length > 1) {
    return explicitLines.map(normalizePolicyInsightBulletLine)
  }

  const normalizedInsightText = normalizePolicyInsightBulletLine(insightText)

  if (!normalizedInsightText.includes('•')) {
    const labelMatches = normalizedInsightText.match(
      /(Policy References:|Compliance Status:|Explanation:)/g,
    )

    if (labelMatches && labelMatches.length > 1) {
      const segments = normalizedInsightText
        .split(/(?=Policy References:|Compliance Status:|Explanation:)/)
        .map((part) => part.trim())
        .filter(Boolean)

      if (segments.length > 1) return segments
    }
  }

  const bulletParts = normalizedInsightText
    .split(/\s*•\s*/)
    .map((part) => part.trim())
    .filter(Boolean)

  if (bulletParts.length <= 1) return [normalizedInsightText]

  const [firstPart, ...remainingParts] = bulletParts
  const startsWithNumberedPrefix = /^\d+[.)]$/.test(firstPart)

  if (startsWithNumberedPrefix && remainingParts.length > 0) {
    const [firstBullet, ...restBullets] = remainingParts
    return [`• ${firstBullet}`, ...restBullets.map((part) => `• ${part}`)]
  }

  if (normalizedInsightText.trimStart().startsWith('•')) {
    return bulletParts.map((part) => `• ${part}`)
  }

  return [firstPart, ...remainingParts.map((part) => `• ${part}`)]
}

const renderPolicyInsightTextLines = (lines: string[]): React.ReactNode[] => {
  const renderedLines: React.ReactNode[] = []
  let index = 0

  while (index < lines.length) {
    const line = lines[index]

    if (!isPolicyInsightBulletLine(line)) {
      renderedLines.push(
        <span className={'policy-insight-text-line'} key={`${index}-${line}`}>
          {line}
        </span>,
      )
      index += 1
      continue
    }

    const bulletLines: string[] = []
    while (index < lines.length && isPolicyInsightBulletLine(lines[index])) {
      bulletLines.push(lines[index].slice(2))
      index += 1
    }

    renderedLines.push(
      <ul className={'policy-insight-text-bullets'} key={`bullets-${index}`}>
        {bulletLines.map((bulletLine, bulletIndex) => (
          <li
            className={'policy-insight-text-line policy-insight-text-bullet'}
            key={`${bulletIndex}-${bulletLine}`}
          >
            {bulletLine}
          </li>
        ))}
      </ul>,
    )
  }

  return renderedLines
}

const getSelectedPolicyFeedbackType = (
  feedbackType: TransactionAiInsightFeedbackType | null | undefined,
): TransactionAiInsightFeedbackType | undefined =>
  feedbackType === TransactionAiInsightFeedbackType.None
    ? undefined
    : (feedbackType ?? undefined)

const getPolicyInsightFeedbackAnalyticsAction = (
  feedbackType: TransactionAiInsightFeedbackType | null | undefined,
): 'approve' | 'reject' | undefined => {
  if (feedbackType === TransactionAiInsightFeedbackType.ThumbsUp) {
    return 'approve'
  }
  if (feedbackType === TransactionAiInsightFeedbackType.ThumbsDown) {
    return 'reject'
  }
  return undefined
}

interface PolicyInsightDisclosureProps {
  insightText: string
  keyPoints?: string
  policyUrl?: string
  feedbackActions?: React.ReactNode
}

const PolicyInsightDisclosure: React.FC<PolicyInsightDisclosureProps> = ({
  insightText,
  keyPoints,
  policyUrl,
  feedbackActions,
}) => {
  const translate = useTranslate()
  const contentId = useId()
  const detailsContentId = useId()
  // Disclosure state defaults are applied per displayed insight: the render
  // site keys this component by transaction/insight identity (and keyPoints
  // presence), so a route :id change or an in-place pusher refetch remounts it
  // and re-derives both defaults instead of stranding stale collapse state.
  const [isCollapsed, setIsCollapsed] = useState(!keyPoints)
  const [showDetails, setShowDetails] = useState(false)

  const policyLink = policyUrl ? (
    <LinkText
      href={policyUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={'policyDetailsLink'}
      aria-label={translate('See Expense Policy document')}
      className={'policy-insight-details-link'}
    >
      {translate('See Expense Policy document')}
      <SvgExternalLink
        width={16}
        height={16}
        viewBox="0 0 24 24"
        fill="currentColor"
      />
    </LinkText>
  ) : null

  return (
    <div className={'expense-policy-ai-banner'} data-testid="rejection-notice">
      <Button
        variant="no-style"
        dropShadow={false}
        onClick={() => setIsCollapsed((collapsed) => !collapsed)}
        testId="policy-insight-collapse-toggle"
        aria-label={translate("Here's your policy info")}
        aria-expanded={!isCollapsed}
        aria-controls={isCollapsed ? undefined : contentId}
        className="policy-insight-collapse-toggle"
      >
        {isCollapsed ? <SvgChevronDown /> : <SvgChevronUp />}
      </Button>
      <div className={'policy-insight-icon'}>
        <AiStarsIcon />
      </div>
      <div className={'policy-insight-body'}>
        <div className={'policy-insight-header'}>
          <div className={'policy-insight-heading'}>
            <span className={'font-md font-weight-medium text-primary'}>
              {translate("Here's your policy info")}
            </span>
            <BetaTag isBetaTagHeader className={'policy-insight-beta-tag'} />
          </div>
          {feedbackActions && (
            <div className={'policy-insight-header-actions'}>
              {feedbackActions}
            </div>
          )}
        </div>
        {!isCollapsed && (
          <div
            className={'policy-insight-content'}
            data-testid="policy-insight-content"
            id={contentId}
          >
            {keyPoints ? (
              <>
                <div
                  className={'policy-insight-key-takeaways'}
                  data-testid="policy-insight-key-takeaways"
                >
                  <span className="policy-insight-key-takeaways-label">
                    {translate('Key takeaways')}
                  </span>
                  <div className={'policy-insight-text'}>
                    {renderPolicyInsightTextLines(
                      getPolicyInsightTextLines(keyPoints).map((line) =>
                        isPolicyInsightBulletLine(line) ? line : `• ${line}`,
                      ),
                    )}
                  </div>
                </div>
                <div className={'policy-insight-footer'}>
                  <Button
                    variant="no-style"
                    dropShadow={false}
                    onClick={() => setShowDetails((expanded) => !expanded)}
                    testId="policy-insight-details-toggle"
                    aria-label={translate('See more details')}
                    aria-expanded={showDetails}
                    aria-controls={showDetails ? detailsContentId : undefined}
                    className="policy-insight-details-toggle"
                  >
                    <span className="policy-insight-details-toggle-chevron">
                      {showDetails ? <SvgChevronUp /> : <SvgChevronDown />}
                    </span>
                    <span>{translate('See more details')}</span>
                  </Button>
                  {policyLink}
                </div>
                {showDetails && (
                  <div
                    className={'policy-insight-text'}
                    data-testid="policy-insight-details-content"
                    id={detailsContentId}
                  >
                    {renderPolicyInsightTextLines(
                      getPolicyInsightTextLines(insightText),
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className={'policy-insight-text'}>
                  {renderPolicyInsightTextLines(
                    getPolicyInsightTextLines(insightText),
                  )}
                </div>
                {policyLink}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

interface TransactionSummarySectionProps {
  transaction: TransactionDetailModel
  cardUser: UserListModel
  timezone: string
  showHeader?: boolean
  showTimeline?: boolean
  showPolicyInsights?: boolean
  showPolicyInsightsHeading?: boolean
}

interface TransactionSummaryHeaderProps {
  transaction: TransactionDetailModel
}

export const TransactionSummaryHeader: React.FC<
  TransactionSummaryHeaderProps
> = ({ transaction }): React.ReactElement => {
  const translate = useTranslate()
  const locale = useSelector(selectNullSafeLocaleFromState)
  const { data: user } = useQueryOwnUser({})
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const syncedToERP = transaction.connectedPlatforms?.find(
    (c) =>
      c.platform === activeIntegration?.connectedPlatform &&
      c.syncStatus === ConnectedPlatformStatus.Success,
  )

  return (
    <div css={style} className={'transactionSummaryHeader'}>
      <div className={'flex-column gap-4'}>
        <div
          className={'flex-row gap-2 align-baseline font-h1 text-primary'}
          data-testid="transaction-amount"
        >
          {transaction.clearingOrAuthLabel(true)}
          {transaction.currency !== getLocaleInfo(locale).currency && (
            <div className={'font-regular text-secondary'}>
              {transaction.currency}
            </div>
          )}
        </div>
        <div className={'details'}>
          <span className={'flex-row gap-1 align-center'}>
            <CardsNewIcon />
            <span
              className={'transaction-card-name'}
              data-testid="transaction-card-name"
            >
              {transaction.vcnDisplayName}
            </span>
            <Tag fontSize={10} text={`${transaction.vcnLast4}`} />
          </span>
          {transaction.clearedAt && (
            <span data-testid="transaction-cleared-date">
              {dateFormatString(transaction.clearedAt, 'l')}
            </span>
          )}
          {transaction.status !== 'DECLINED' && (
            <span data-testid="transaction-status">
              {transaction.pseudoStatusLabelNew}
            </span>
          )}
          {transaction.status === 'DECLINED' && transaction.declineReason && (
            <span className={'flex-row'} data-testid="transaction-status">
              <Tooltip tooltipContent={transaction.declineReason}>
                <span className={'decline-reason'}>
                  {transaction.declineReason}
                </span>
              </Tooltip>
            </span>
          )}
          {user?.hasOrgBookkeeperPermissions && syncedToERP && (
            <TooltipIcon
              icon={
                activeIntegration?.iconElement ? (
                  <span
                    css={css`
                      ${iconStyle};
                      display: inline-flex;
                      align-items: center;
                      justify-content: center;

                      svg {
                        width: 100%;
                        height: 100%;
                      }
                    `}
                    aria-hidden="true"
                  >
                    {activeIntegration.iconElement}
                  </span>
                ) : (
                  <img css={iconStyle} src={activeIntegration?.logo} alt={''} />
                )
              }
            >
              {translate('Synced to ${orgLvlIntegration}', {
                orgLvlIntegration: activeIntegration?.platformName || '',
              })}
            </TooltipIcon>
          )}
        </div>
      </div>
    </div>
  )
}

const TransactionSummarySection: React.FC<TransactionSummarySectionProps> = ({
  transaction,
  cardUser,
  timezone,
  showHeader = true,
  showTimeline = true,
  showPolicyInsights = true,
  showPolicyInsightsHeading = false,
}): React.ReactElement => {
  const translate = useTranslate()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const requestConfig = useRequestConfig()
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const { analytics } = useAnalyticsContext()
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false)
  const [hasFeedbackBeenProvided, setHasFeedbackBeenProvided] = useState(false)
  const [
    isPolicyInsightFeedbackSubmitting,
    setIsPolicyInsightFeedbackSubmitting,
  ] = useState(false)
  const [selectedPolicyFeedbackType, setSelectedPolicyFeedbackType] = useState<
    TransactionAiInsightFeedbackType | undefined
  >()
  const merchantImageSrc =
    typeof transaction.merchantImageSrc === 'function'
      ? transaction.merchantImageSrc('medium')
      : undefined
  const { data: user } = useQueryOwnUser({})
  const aiPolicyAssistantEnabled = useAIPolicyAssistantEnabled()
  const featureFlags = useSelector(selectFeatureFlaggerFromState)
  const showExpensePolicyAI = isExpensePolicyAIEnabled(
    featureFlags,
    user?.organizationId,
  )

  const { data: policy } = useQueryExpensePolicy({
    orgId: organization?.id,
    enabled: aiPolicyAssistantEnabled,
  })
  const putPolicyInsightFeedbackMutation =
    usePutTransactionAiInsightFeedbackMutation({
      onSuccess: (data) => {
        setSelectedPolicyFeedbackType(
          getSelectedPolicyFeedbackType(data.feedbackType),
        )
        const feedbackAction = getPolicyInsightFeedbackAnalyticsAction(
          data.feedbackType,
        )
        if (feedbackAction !== undefined) {
          analytics?.track('AI policy insight feedback', {
            action: feedbackAction,
            transaction_id: transaction.id,
            transaction_url: window.location.href,
          })
        }
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: (
              <span>{translate('Thank you for your feedback!')}</span>
            ),
          }),
        )
      },
      onSettled: () => {
        setIsPolicyInsightFeedbackSubmitting(false)
      },
    })
  const isPolicyInsightFeedbackDisabled =
    isPolicyInsightFeedbackSubmitting ||
    putPolicyInsightFeedbackMutation.isPending

  const handlePositiveFeedback = () => {
    analytics?.track('transaction detail - normalized merchant - positive', {
      transactionId: transaction.id,
      normalizedMerchantName:
        transaction.normalizedMerchant?.normalizedMerchantName ?? '',
      originalMerchantName: transaction.merchantName ?? '',
    })

    setHasFeedbackBeenProvided(true)

    dispatch(
      createSetIsToastOpen(true, {
        toastMessage: <span>{translate('Thank you for your feedback!')}</span>,
      }),
    )
  }

  const handleNegativeFeedback = () => {
    setIsFeedbackModalOpen(true)
  }

  const handleFeedbackSubmit = (feedback: string) => {
    analytics?.track('transaction detail - normalized merchant - negative', {
      transactionId: transaction.id,
      normalizedMerchantName:
        transaction.normalizedMerchant?.normalizedMerchantName ?? '',
      originalMerchantName: transaction.merchantName ?? '',
      userFeedback: feedback,
    })

    setHasFeedbackBeenProvided(true)

    dispatch(
      createSetIsToastOpen(true, {
        toastMessage: <span>{translate('Thank you for your feedback!')}</span>,
      }),
    )
  }

  const policyInsights = useMemo(
    () => transaction.transactionAiInsights?.filter(isPolicyAgentInsight) ?? [],
    [transaction.transactionAiInsights],
  )
  const policyInsight = useMemo(
    () =>
      policyInsights.find((insight) => Boolean(insight.insightText)) ??
      policyInsights[0],
    [policyInsights],
  )
  const feedbackPolicyInsight = useMemo(
    () =>
      findPersistedPolicyAgentInsightForDisplay(policyInsights, policyInsight),
    [policyInsight, policyInsights],
  )

  useEffect(() => {
    setSelectedPolicyFeedbackType(
      getSelectedPolicyFeedbackType(feedbackPolicyInsight?.feedbackType),
    )
  }, [feedbackPolicyInsight?.feedbackType, feedbackPolicyInsight?.id])

  const canRenderPolicyInsightFeedback =
    Boolean(user?.hasOrgBookkeeperPermissions) &&
    Boolean(policyInsight?.insightText)

  const handlePolicyInsightFeedback = (
    feedbackType: TransactionAiInsightFeedbackType,
  ) => {
    if (isPolicyInsightFeedbackDisabled) {
      return
    }

    const nextFeedbackType =
      selectedPolicyFeedbackType === feedbackType
        ? TransactionAiInsightFeedbackType.None
        : feedbackType

    setIsPolicyInsightFeedbackSubmitting(true)

    const submitFeedback = async (): Promise<void> => {
      let feedbackInsightId = feedbackPolicyInsight?.id

      if (!feedbackInsightId) {
        const refreshedTransaction = await queryTransactionDetail(
          queryClient,
          requestConfig as AxiosRequestConfig,
          transaction.id,
          false,
        )
        const refreshedPolicyInsights =
          refreshedTransaction?.transactionAiInsights?.filter(
            isPolicyAgentInsight,
          ) ?? []

        feedbackInsightId = findPersistedPolicyAgentInsightForDisplay(
          refreshedPolicyInsights,
          policyInsight,
        )?.id
      }

      if (!feedbackInsightId) {
        setIsPolicyInsightFeedbackSubmitting(false)
        return
      }

      putPolicyInsightFeedbackMutation.mutate({
        transactionId: transaction.id,
        insightId: feedbackInsightId,
        body: { feedbackType: nextFeedbackType },
      })
    }

    void submitFeedback()
  }

  return (
    <div css={style} className={'transactionSummarySection'}>
      {showHeader && <TransactionSummaryHeader transaction={transaction} />}
      {showTimeline && (
        <div className={'timeline'}>
          <div className={'timeline-event'}>
            <MerchantIcon
              size={36}
              merchantImgSrc={merchantImageSrc}
              mccGroupKey={transaction.mccGroupKey ?? 'OTHER'}
            />
            <div className={'flex-column'}>
              <div
                className={'font-h3 text-primary flex-row gap-1 align-center'}
                data-testid="merchant-name"
              >
                {transaction.normalizedMerchantNameOrUnknown}
                {transaction.normalizedMerchant && (
                  <>
                    <TooltipIcon iconTestId="original-merchant-tooltip-icon">
                      {translate('Original Merchant: ${merchantName}', {
                        merchantName:
                          transaction.merchantName ??
                          translate('Unknown Merchant'),
                      })}
                    </TooltipIcon>
                    {!hasFeedbackBeenProvided && (
                      <>
                        <Tooltip
                          tooltipContent={translate(
                            'This merchant name is correct',
                          )}
                        >
                          <Button
                            variant="toolbar"
                            dropShadow={false}
                            onClick={handlePositiveFeedback}
                            testId="normalized-merchant-thumbs-up"
                            aria-label="Thumbs up"
                            className="feedback-button positive"
                          >
                            <ThumbsUpIcon />
                          </Button>
                        </Tooltip>
                        <Tooltip
                          tooltipContent={translate(
                            'This merchant name is incorrect',
                          )}
                        >
                          <Button
                            variant="toolbar"
                            dropShadow={false}
                            onClick={handleNegativeFeedback}
                            testId="normalized-merchant-thumbs-down"
                            aria-label="Thumbs down"
                            className="feedback-button negative"
                          >
                            <ThumbsDownIcon />
                          </Button>
                        </Tooltip>
                      </>
                    )}
                  </>
                )}
              </div>
              <div className={'font-md text-secondary'} data-testid="mcc-group">
                {transaction.mccGroup}
              </div>
            </div>
          </div>
          <div className={'timeline-event'}>
            <UserAvatar user={cardUser} size={AvatarSizes.MediumLarge} />
            <div className={'flex-column'}>
              <div className={'font-h3 text-primary'}>
                <span data-testid="card-user-name">
                  {transaction.recipientName}
                </span>
              </div>
              {transaction.date && (
                <div
                  className={'font-md text-secondary'}
                  data-testid="transaction-auth-date-time"
                >
                  {convertUTCTimezone(timezone, transaction.date).format(
                    'l, LT',
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {showPolicyInsights &&
        showExpensePolicyAI &&
        aiPolicyAssistantEnabled &&
        policyInsight?.insightText && (
          <>
            {showPolicyInsightsHeading && (
              <span className={'font-h3'}>
                {translate('AI Policy Context')}
              </span>
            )}
            <PolicyInsightDisclosure
              key={`${transaction.id}:${policyInsight.id ?? ''}:${
                policyInsight.keyPoints ? 'kp' : 'nokp'
              }`}
              insightText={policyInsight.insightText}
              keyPoints={policyInsight.keyPoints}
              policyUrl={policy?.urls.original}
              feedbackActions={
                canRenderPolicyInsightFeedback ? (
                  <div className={'policy-feedback-actions'}>
                    <Tooltip
                      tooltipContent={translate('This insight is helpful')}
                    >
                      <Button
                        variant="no-border"
                        dropShadow={false}
                        onClick={() =>
                          handlePolicyInsightFeedback(
                            TransactionAiInsightFeedbackType.ThumbsUp,
                          )
                        }
                        testId="policy-insight-thumbs-up"
                        aria-label={translate('This insight is helpful')}
                        aria-pressed={
                          selectedPolicyFeedbackType ===
                          TransactionAiInsightFeedbackType.ThumbsUp
                        }
                        aria-busy={isPolicyInsightFeedbackDisabled}
                        aria-disabled={isPolicyInsightFeedbackDisabled}
                        className={`policy-feedback-button${
                          selectedPolicyFeedbackType ===
                          TransactionAiInsightFeedbackType.ThumbsUp
                            ? ' selected'
                            : ''
                        }`}
                      >
                        <PolicyInsightThumbsUpIcon />
                      </Button>
                    </Tooltip>
                    <Tooltip
                      tooltipContent={translate('This insight is not helpful')}
                    >
                      <Button
                        variant="no-border"
                        dropShadow={false}
                        onClick={() =>
                          handlePolicyInsightFeedback(
                            TransactionAiInsightFeedbackType.ThumbsDown,
                          )
                        }
                        testId="policy-insight-thumbs-down"
                        aria-label={translate('This insight is not helpful')}
                        aria-pressed={
                          selectedPolicyFeedbackType ===
                          TransactionAiInsightFeedbackType.ThumbsDown
                        }
                        aria-busy={isPolicyInsightFeedbackDisabled}
                        aria-disabled={isPolicyInsightFeedbackDisabled}
                        className={`policy-feedback-button${
                          selectedPolicyFeedbackType ===
                          TransactionAiInsightFeedbackType.ThumbsDown
                            ? ' selected'
                            : ''
                        }`}
                      >
                        <PolicyInsightThumbsDownIcon />
                      </Button>
                    </Tooltip>
                  </div>
                ) : undefined
              }
            />
          </>
        )}

      <NormalizedMerchantFeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
        onSubmit={handleFeedbackSubmit}
        normalizedMerchantName={
          transaction.normalizedMerchant?.normalizedMerchantName ?? ''
        }
        originalMerchantName={transaction.merchantName ?? ''}
      />
    </div>
  )
}

export default TransactionSummarySection
